import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarNotesService } from '@/services/calendar-notes.service';
import { CalendarNote, CreateNoteData, UpdateNoteData } from '@/types/calendar-notes';
import { invalidateCalendarOverlay } from '@/lib/queryInvalidation';

/**
 * Calendar Notes Hooks (Campaigns)
 *
 * React Query hooks for calendar notes/campaigns management
 */

/**
 * Fetch calendar notes for a specific calendar and date range
 */
export const useCalendarNotes = (calendarId: number, startDate: string, endDate: string) =>
  useQuery({
    queryKey: ['calendar-notes', calendarId, startDate, endDate],
    queryFn: () => calendarNotesService.listNotes(calendarId, startDate, endDate),
    enabled: calendarId > 0 && !!startDate && !!endDate,
  });

/**
 * Fetch calendar notes for multiple calendars in parallel and merge results.
 * Replaces multiple `useCalendarNotes` calls when "all calendars" is selected.
 */
export const useCalendarNotesMulti = (calendarIds: number[], startDate: string, endDate: string) => {
  const queries = useQueries({
    queries: calendarIds.map((id) => ({
      queryKey: ['calendar-notes', id, startDate, endDate],
      queryFn: () => calendarNotesService.listNotes(id, startDate, endDate),
      enabled: id > 0 && !!startDate && !!endDate,
    })),
  });

  const data: CalendarNote[] = queries.flatMap((q) => q.data ?? []);
  const isLoading = queries.some((q) => q.isLoading);
  return { data, isLoading };
};

/**
 * Create a new calendar note (campaign)
 */
export const useCreateCalendarNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNoteData) => calendarNotesService.createNote(data),
    onSuccess: () => {
      invalidateCalendarOverlay(queryClient);
    },
  });
};

/**
 * Update a calendar note (campaign)
 */
export const useUpdateCalendarNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ calendarId, noteId, data }: { calendarId: number; noteId: number; data: UpdateNoteData }) =>
      calendarNotesService.updateNote(calendarId, noteId, data),
    onSuccess: () => {
      invalidateCalendarOverlay(queryClient);
    },
  });
};

/**
 * Delete a calendar note (campaign)
 */
export const useDeleteCalendarNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ calendarId, noteId }: { calendarId: number; noteId: number }) =>
      calendarNotesService.deleteNote(calendarId, noteId),
    onSuccess: () => {
      invalidateCalendarOverlay(queryClient);
    },
  });
};
